import { Button } from "@/components/ui/button";
import { ArrowRight, Check, Play, Sparkles, TrendingUp, Timer, Clock, ChevronDown, Star } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "@/i18n";

const Hero = () => {
  const { t } = useTranslation();

  return (
    <section className="relative pt-36 pb-12 md:pt-44 md:pb-32 overflow-hidden">
      {/* Background with glow orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="glow-orb glow-orb-primary w-[600px] h-[600px] -top-40 -left-40 opacity-60" />
        <div className="glow-orb glow-orb-accent w-[500px] h-[500px] top-20 -right-40 opacity-50" />
        <div className="glow-orb glow-orb-primary w-[400px] h-[400px] bottom-0 left-1/2 -translate-x-1/2 opacity-30" />
        <div className="absolute inset-0 grid-pattern opacity-50" />
      </div>

      <div className="container mx-auto px-4 relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Column - Text */}
          <div className="text-left space-y-6">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card text-sm font-medium animate-fade-up">
              <Sparkles size={16} className="text-primary" />
              <span className="text-foreground">{t('hero.badgeMl')}</span>
            </div>

            {/* Headline */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl xl:text-6xl font-extrabold text-foreground leading-tight animate-fade-up opacity-0 delay-100">
              {t('hero.title')}{' '}
              <span className="gradient-text">{t('hero.titleHighlight')}</span>
            </h1>

            {/* Subheadline */}
            <div className="space-y-1 animate-fade-up opacity-0 delay-200">
              <p className="text-base sm:text-lg md:text-xl text-muted-foreground">
                {t('hero.subtitle')}
              </p>
              <p className="text-lg md:text-xl">
                <strong className="text-foreground">{t('hero.subtitleBold')}</strong>
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-start gap-3 animate-fade-up opacity-0 delay-300">
              <Link to="/auth?mode=signup" className="w-full sm:w-auto">
                <Button variant="cta" size="xl" className="group w-full sm:w-auto">
                  {t('hero.cta')}
                  <ArrowRight size={20} className="transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
              <Button variant="hero-outline" size="xl" asChild className="w-full sm:w-auto">
                <a href="#how-it-works" className="gap-2">
                  <Play size={16} className="fill-current" />
                  {t('hero.ctaSecondary')}
                </a>
              </Button>
            </div>

            {/* Trust indicators - inline with checkmarks */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted-foreground animate-fade-up opacity-0 delay-400">
              <span className="flex items-center gap-1.5">
                <Check size={16} className="text-[hsl(142,71%,45%)]" />
                {t('hero.trust1')}
              </span>
              <span className="flex items-center gap-1.5">
                <Check size={16} className="text-[hsl(142,71%,45%)]" />
                {t('hero.trust2')}
              </span>
              <span className="flex items-center gap-1.5">
                <Check size={16} className="text-[hsl(142,71%,45%)]" />
                {t('hero.trust3')}
              </span>
            </div>

            {/* Guarantee line */}
            <p className="text-sm text-muted-foreground animate-fade-up opacity-0 delay-400">
              <strong className="text-foreground">{t('hero.guarantee')}</strong>
            </p>

            {/* Social proof */}
            <p className="text-sm text-muted-foreground animate-fade-up opacity-0 delay-500">
              <span className="font-semibold text-foreground">{t('hero.socialProofLine')}</span>
            </p>
          </div>

          {/* Right Column - Preview */}
          <div className="relative animate-fade-up opacity-0 delay-500 hidden lg:block">
            {/* Floating badge */}
            <div className="absolute -top-3 -right-2 z-10 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-accent/90 text-accent-foreground text-xs font-bold shadow-lg backdrop-blur-sm">
              <Star size={14} className="fill-current" />
              {t('hero.preview.impactBulk')}
            </div>

            {/* Browser card */}
            <div className="rounded-2xl overflow-hidden glass-card gradient-border-animated">
              <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 via-accent/20 to-primary/20 blur-3xl opacity-50" />

              <div className="relative">
                {/* Browser chrome */}
                <div className="flex items-center gap-2 px-4 py-3 bg-muted/30 border-b border-border/30">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-destructive/60" />
                    <div className="w-3 h-3 rounded-full bg-accent/60" />
                    <div className="w-3 h-3 rounded-full bg-primary/60" />
                  </div>
                  <div className="flex-1 mx-4">
                    <div className="h-7 bg-background/50 backdrop-blur-sm rounded-lg flex items-center px-3 border border-border/30">
                      <span className="text-xs text-muted-foreground">{t('hero.preview.url')}</span>
                    </div>
                  </div>
                </div>

                {/* Before/After stacked vertically */}
                <div className="p-5 md:p-6 bg-gradient-to-br from-background/80 to-muted/20 space-y-3">
                  {/* Before - hidden on small mobile */}
                  <div className="hidden sm:block p-4 rounded-xl bg-muted/30 border border-border/30">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-destructive/10 text-destructive border border-destructive/20">{t('hero.preview.before')}</span>
                      <span className="text-xs text-muted-foreground">{t('hero.preview.beforeLabel')}</span>
                    </div>
                    <p className="font-medium text-foreground/70 line-through text-sm mb-1">{t('hero.preview.beforeTitle')}</p>
                    <p className="text-xs text-muted-foreground/70 line-through mb-2">{t('hero.preview.beforeDesc')}</p>
                    <div className="flex flex-wrap gap-3">
                      <span className="text-xs text-destructive/80">❌ Google no entiende</span>
                      <span className="text-xs text-destructive/80">❌ No posiciona</span>
                    </div>
                  </div>

                  {/* Arrow down - hidden on small mobile */}
                  <div className="hidden sm:flex justify-center">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center animate-bounce">
                      <ChevronDown size={18} className="text-primary" />
                    </div>
                  </div>

                  {/* After */}
                  <div className="p-4 rounded-xl bg-primary/5 border border-primary/20 gradient-border">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">{t('hero.preview.after')}</span>
                      <span className="text-xs text-muted-foreground">{t('hero.preview.afterLabel')}</span>
                    </div>
                    <p className="font-medium text-foreground text-sm mb-1">{t('hero.preview.afterTitle')}</p>
                    <p className="text-xs text-muted-foreground mb-2">{t('hero.preview.afterDesc')}</p>
                    <div className="flex flex-wrap gap-3">
                      <span className="text-xs text-primary">✅ Posiciona en búsquedas</span>
                      <span className="text-xs text-primary">✅ Más clics, más ventas</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 3 Stats below card */}
            <div className="grid grid-cols-3 gap-3 mt-4">
              <div className="text-center p-3 rounded-xl glass-card">
                <p className="text-xl font-bold gradient-text">{t('hero.preview.impactScore')}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">{t('hero.preview.statScore')}</p>
              </div>
              <div className="text-center p-3 rounded-xl glass-card">
                <p className="text-xl font-bold text-accent">{t('hero.preview.impactTime')}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">{t('hero.preview.statTime')}</p>
              </div>
              <div className="text-center p-3 rounded-xl glass-card">
                <p className="text-xl font-bold text-foreground">{t('hero.preview.impactSaved')}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">{t('hero.preview.statSaved')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
